import { useEffect, useState } from 'react';
import { PRONUNCIATION_GROUPS, PRONUNCIATION_TERMS, type PronunciationTerm } from '@shared/pronunciation';
import { SpeakerIcon } from '@/components/Icons';
import { canSpeak, speakEnglishPronunciation, stopSpeaking } from '@/lib/speak';

export function PronunciationPage() {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState(() => !canSpeak());

  useEffect(() => () => stopSpeaking(), []);

  function play(term: PronunciationTerm) {
    const result = speakEnglishPronunciation(term.say, {
      onEnd: () => setPlayingId((id) => (id === term.id ? null : id)),
      onError: () => {
        setPlayingId((id) => (id === term.id ? null : id));
        setUnsupported(true);
      },
    });
    if (result === 'unsupported') {
      setUnsupported(true);
      setPlayingId(null);
      return;
    }
    setUnsupported(false);
    setPlayingId(term.id);
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Pronunciation guide</h1>
        <p className="page-subtitle">Tap Play to hear the English pronunciation — stays on this page</p>
      </div>
      {unsupported ? (
        <p className="muted" role="status">
          This browser cannot play speech here. Try Chrome with sound on.
        </p>
      ) : null}
      {PRONUNCIATION_GROUPS.map((group) => {
        const terms = PRONUNCIATION_TERMS.filter((t) => t.group === group.id);
        return (
          <section key={group.id}>
            <h2 className="section-title">{group.label}</h2>
            <div className="card pronounce-card">
              {terms.map((term) => {
                const playing = playingId === term.id;
                return (
                  <div key={term.id} className={`pronounce-row${playing ? ' is-playing' : ''}`}>
                    <span className="pronounce-row__copy">
                      <strong>{term.term}</strong>
                      {term.group === 'places' ? (
                        <small className="muted">
                          {term.phonetic} · {term.meaning}
                        </small>
                      ) : (
                        <>
                          <span className="bilingual__en">{term.meaning}</span>
                          <small className="muted">{term.phonetic}</small>
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn pronounce-play"
                      aria-label={`Play English pronunciation of ${term.term}`}
                      aria-pressed={playing}
                      onClick={() => play(term)}
                    >
                      <SpeakerIcon width={16} height={16} />
                      {playing ? 'Playing' : 'Play'}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
